import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';

import {
  absenceDecisionReason,
  fetchAbsenceRequests,
  fetchAttendance,
} from '@/features/events/attendanceApi';
import { eventSurveyTabs } from '@/features/events/eventSurveyTabs';
import { fetchEventSurveys } from '@/features/surveys/api';
import { formatDateTime } from '@/lib/datetime';

interface Todo {
  done: boolean;
  label: string;
  detail: string;
  tab: string;
  actionLabel: string;
}

function deadlineText(deadlineAt: string | null): string {
  if (!deadlineAt) return '期限は決まっていません';
  return `期限 ${formatDateTime(deadlineAt)}`;
}

/**
 * 受講生向けの「次にやること」（D-179）.
 *
 * 事務局には下書きイベントで `EventNextSteps` が出るのに対し、受講生側には
 * 「このイベントで自分が何をすればよいか」がどこにも出ていなかった
 * （実機フィードバック「イベントに対してアクションしないといけない事は
 * 事務局のように書いておいて」）。出欠・課題・アンケート各タブについて、
 * 未対応のものだけが目立つ形で並べる。
 *
 * 面談のうちサクセス面談は出さない。1on1は事後アンケートだけ載せる（D-271）。
 * 出欠の取得は呼び出し側で止める（面談の出欠APIは409 / D-177）。
 */
export function EventStudentTodo({
  eventId,
  attendanceRequired,
  typeCode,
  capacity,
  capacityFull,
  onGoToTab,
}: {
  eventId: string;
  attendanceRequired: boolean;
  /** 種別コード（月例会はタブ名が異なる / D-329） */
  typeCode?: string | null;
  capacity?: number | null;
  capacityFull?: boolean;
  onGoToTab: (tabId: string) => void;
}): ReactElement | null {
  const attendance = useQuery({
    queryKey: ['attendance', eventId],
    queryFn: () => fetchAttendance(eventId),
    enabled: attendanceRequired,
  });
  const surveys = useQuery({
    queryKey: ['event-surveys', eventId],
    queryFn: () => fetchEventSurveys(eventId),
  });
  const absences = useQuery({
    queryKey: ['absence-requests', eventId],
    queryFn: () => fetchAbsenceRequests(eventId),
    enabled: attendanceRequired,
  });

  if (
    (attendanceRequired && attendance.isPending) ||
    surveys.isPending ||
    (attendanceRequired && absences.isPending)
  ) {
    return null;
  }

  // 出欠の行は公開時に対象者ぶんだけ作られる（D-115）。行が無い＝この回の対象外
  const rows = attendance.data ?? [];
  if (attendanceRequired && rows.length === 0) return null;

  const todos: Todo[] = [];

  if (attendanceRequired) {
    const absenceLocked = (absences.data ?? []).some(
      (r) => r.status === 'pending' || r.status === 'approved',
    );
    const rejected = [...(absences.data ?? [])]
      .filter((r) => r.status === 'rejected')
      .at(-1);
    const unanswered = rows.filter((r) => r.status === 'unanswered' && !r.waitlisted);
    if (rejected && !absenceLocked && unanswered.length > 0) {
      const reason = absenceDecisionReason(rejected);
      todos.push({
        done: false,
        label: '欠席申請が差し戻されました',
        detail: reason
          ? `差戻し理由: ${reason}。出欠を選び直してください`
          : '出欠を選び直してください',
        tab: 'attendance',
        actionLabel: '「出欠」タブへ',
      });
    } else if (!absenceLocked) {
      const fcfsFull =
        typeCode === 'subcommittee' && capacity != null && capacityFull === true;
      const mainRow = rows.find((r) => r.part === 'main');
      const alreadyAttending =
        mainRow !== undefined &&
        (mainRow.status === 'onsite' ||
          mainRow.status === 'online' ||
          mainRow.status === 'late');
      todos.push({
        done: unanswered.length === 0,
        label: fcfsFull && !alreadyAttending ? '定員に達しています' : '出欠を申請する',
        detail:
          fcfsFull && !alreadyAttending
            ? `定員（${capacity}名）の参加枠は埋まっています。欠席のみ申請できます`
            : unanswered.length === 0
              ? '申請済みです（あとから選び直せます）'
              : 'まだ申請していません。参加のしかたを選んで申請してください',
        tab: 'attendance',
        actionLabel: '「出欠」タブへ',
      });
    }
  }

  // 提出状況は受講生には自分の分だけが返る（API 側で行スコープ済み / §6）
  const list = surveys.data ?? [];
  for (const group of eventSurveyTabs(typeCode)) {
    if (group.kind === 'director') continue;
    const items = list.filter((s) => s.template_kind === group.kind);
    if (items.length === 0) continue;
    const remaining = items.filter((s) => s.submitted_count === 0);
    todos.push({
      done: remaining.length === 0,
      label: `${group.label}に回答する`,
      detail:
        remaining.length === 0
          ? `${items.length}件すべて回答済みです`
          : `未回答が${remaining.length}件あります（${remaining
              .map((s) => `${s.template_name}・${deadlineText(s.deadline_at)}`)
              .join(' / ')}）`,
      tab: group.tabId,
      actionLabel: `「${group.label}」タブへ`,
    });
  }

  const attendanceNote = !attendanceRequired ? (
    <p className="mb-2 text-wptext-muted">この回は出欠を取りません。参加・欠席の申請は不要です。</p>
  ) : null;

  if (todos.length === 0) {
    if (attendanceNote === null) return null;
    return (
      <section className="mb-4 rounded-sm border border-wpborder bg-white px-4 py-3">
        {attendanceNote}
      </section>
    );
  }
  const remaining = todos.filter((t) => !t.done).length;

  return (
    <section
      className={`mb-4 rounded-sm border px-4 py-3 ${
        remaining > 0
          ? 'border-wpnotice-warning bg-wpnotice-warning/10'
          : 'border-wpnotice-success bg-wpnotice-success/10'
      }`}
    >
      {attendanceNote}
      <h2 className="mb-1 font-semibold text-wptext-strong">
        {remaining > 0 ? `次にやること（未対応 ${remaining}件）` : '次にやること（すべて対応済みです）'}
      </h2>
      <ol className="flex flex-col gap-2">
        {todos.map((todo) => (
          <li key={todo.label} className="flex flex-wrap items-center gap-2">
            <span
              aria-hidden="true"
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                todo.done ? 'bg-wpnotice-success text-white' : 'bg-wpborder text-wptext-strong'
              }`}
            >
              {todo.done ? '✓' : '!'}
            </span>
            <span className={todo.done ? 'text-wptext-muted' : 'font-semibold text-wptext-strong'}>
              {todo.label}
            </span>
            <span className="text-wptext-muted">— {todo.detail}</span>
            <button
              type="button"
              onClick={() => onGoToTab(todo.tab)}
              className="text-wpaccent hover:underline"
            >
              {todo.actionLabel}
            </button>
          </li>
        ))}
      </ol>
    </section>
  );
}
